"use client";

import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { UserSubscriptionFeature } from "@/types/userSubscription.types";

interface SubscriptionFeaturePickerProps {
  features: UserSubscriptionFeature[];
  loading: boolean;
  selectedFeatureIds: string[];
  // Features already recorded on the appointment when the editor opened
  initialFeatureIds: string[];
  onToggle: (featureId: string) => void;
  disabled?: boolean;
}

// A feature can't be newly used when its balance is 0 or the plan doesn't
// include it. One already used on this appointment stays selectable so it
// can be unticked.
export function getFeatureUnavailableReason(
  feature: UserSubscriptionFeature,
  initialFeatureIds: string[],
): string | null {
  if (initialFeatureIds.includes(feature.id)) return null;
  if (feature.type === "Boolean") {
    return feature.value === true ? null : "Not in plan";
  }
  return Number(feature.value) > 0 ? null : "Used up";
}

function isNumericValue(value: UserSubscriptionFeature["value"]): boolean {
  if (typeof value === "number") return true;
  return typeof value === "string" && value !== "" && !isNaN(Number(value));
}

export function SubscriptionFeaturePicker({
  features,
  loading,
  selectedFeatureIds,
  initialFeatureIds,
  onToggle,
  disabled = false,
}: SubscriptionFeaturePickerProps) {
  if (loading) {
    return (
      <div className="p-4 text-center text-muted-foreground">
        Loading features...
      </div>
    );
  }

  if (features.length === 0) {
    return (
      <div className="p-4 text-center text-muted-foreground">
        No features available
      </div>
    );
  }

  return (
    <>
      <div className="space-y-2 max-h-[300px] overflow-y-auto">
        {features.map((feature) => {
          const isSelected = selectedFeatureIds.includes(feature.id);
          const unavailableReason = getFeatureUnavailableReason(
            feature,
            initialFeatureIds,
          );
          const isUsedHere = initialFeatureIds.includes(feature.id);
          const isBoolean = feature.type === "Boolean";

          let valueLabel = (
            <span>Type: {feature.type}</span>
          );
          if (isBoolean) {
            valueLabel = (
              <span
                className={feature.value ? "text-green-600" : "text-gray-500"}
              >
                {feature.value ? "Enabled" : "Disabled"}
              </span>
            );
          } else if (isNumericValue(feature.value)) {
            valueLabel = <span>Balance: {feature.value}</span>;
          }

          return (
            <div
              key={feature.id}
              className="flex items-center justify-between p-3 border rounded hover:bg-muted/50 transition-colors"
            >
              <div className="flex items-center gap-3 flex-1">
                <Checkbox
                  id={`feature-${feature.id}`}
                  checked={isSelected}
                  disabled={disabled || unavailableReason !== null}
                  onCheckedChange={() => onToggle(feature.id)}
                />
                <div className="flex-1">
                  <Label
                    htmlFor={`feature-${feature.id}`}
                    className="text-sm font-medium cursor-pointer"
                  >
                    {feature.name}
                  </Label>
                  {unavailableReason && (
                    <Badge variant="outline" className="ml-2">
                      {unavailableReason}
                    </Badge>
                  )}
                  {isUsedHere && (
                    <Badge variant="secondary" className="ml-2">
                      Used on this appointment
                    </Badge>
                  )}
                  <div className="text-xs text-muted-foreground mt-1">
                    {valueLabel}
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {selectedFeatureIds.length > 0 && (
        <p className="text-xs text-muted-foreground mt-2">
          {selectedFeatureIds.length} feature(s) selected
        </p>
      )}
    </>
  );
}
